import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import heroImage from '../../assets/installerAppHero.jpg';

import css from './InstallerAppPage.module.css';

/**
 * Guide page explaining how to install the marketplace as a PWA
 * (Add to Home Screen) on iPhone (Safari) and Android (Chrome).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @returns {JSX.Element} the InstallerAppPage component
 */
// MassageDerniereMinute: this page's text isn't wired through the usual translations/*.json
// system, so it's translated here directly based on the deployment's locale.
const isEnglish = process.env.REACT_APP_FORCE_LOCALE === 'en';

export const InstallerAppPageComponent = props => {
  const { scrollingDisabled } = props;

  const brandName = isEnglish ? (
    <>
      Same
      <wbr />
      Day
      <wbr />
      Massage
    </>
  ) : (
    <>
      Massage
      <wbr />
      Dernière
      <wbr />
      Minute
    </>
  );
  const marketplaceDomain = isEnglish ? 'samedaymassage.ca' : 'massagederniereminute.com';
  const contactEmail = isEnglish ? 'info@samedaymassage.ca' : 'info@massagederniereminute.com';

  return (
    <Page
      title={
        isEnglish
          ? 'Install the app — SameDayMassage.ca'
          : "Installer l'application — MassageDernièreMinute.com"
      }
      scrollingDisabled={scrollingDisabled}
    >
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Figtree:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <img src={heroImage} alt="" aria-hidden="true" className={css.heroBanner} />

          <section className={css.pageHeader}>
            <div className={css.headerBadge}>{isEnglish ? 'New' : 'Nouveau'}</div>
            <h1>
              {isEnglish ? 'Install ' : 'Installez '}
              <span className={css.brand}>{brandName}</span>
              <br />
              {isEnglish ? 'on your phone' : 'sur votre téléphone'}
            </h1>
            <p>
              {isEnglish
                ? 'Add the site to your home screen for one-tap access, just like a real app — free, no App Store or Google Play needed.'
                : "Ajoutez le site à votre écran d'accueil pour y accéder en un seul geste, comme une vraie application — gratuit, sans passer par l'App Store ou Google Play."}
            </p>
          </section>

          <div className={css.main}>
            {/* BENEFITS */}
            <article className={css.step} aria-labelledby="benefits-title">
              <div className={css.stepHeader}>
                <div className={css.stepNumber} aria-hidden="true">
                  ✓
                </div>
                <h2 className={css.stepTitle} id="benefits-title">
                  {isEnglish ? 'Why install it?' : "Pourquoi l'installer ?"}
                </h2>
                <span className={css.stepIcon} aria-hidden="true">
                  📱
                </span>
              </div>
              <ul className={css.benefits}>
                <li>
                  <span className={css.checkBox} aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    {isEnglish
                      ? 'An icon right on your home screen'
                      : "Une icône directement sur votre écran d'accueil"}
                  </span>
                </li>
                <li>
                  <span className={css.checkBox} aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    {isEnglish
                      ? 'Instant, full-screen opening, no browser bar'
                      : 'Ouverture instantanée, en plein écran, sans barre de navigateur'}
                  </span>
                </li>
                <li>
                  <span className={css.checkBox} aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    {isEnglish
                      ? 'No download — takes 10 seconds'
                      : 'Aucun téléchargement — ça prend 10 secondes'}
                  </span>
                </li>
                <li>
                  <span className={css.checkBox} aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    {isEnglish
                      ? 'Always the latest version of the site'
                      : 'Toujours la version la plus à jour du site'}
                  </span>
                </li>
              </ul>
            </article>

            {/* IPHONE */}
            <article className={css.step} aria-labelledby="iphone-title">
              <div className={css.stepHeader}>
                <div className={css.stepNumber} aria-hidden="true">
                  1
                </div>
                <h2 className={css.stepTitle} id="iphone-title">
                  {isEnglish ? 'On iPhone (Safari)' : 'Sur iPhone (Safari)'}
                </h2>
                <span className={css.stepIcon} aria-hidden="true">
                  🍎
                </span>
              </div>
              <ol
                className={css.substeps}
                aria-label={isEnglish ? 'Instructions for iPhone' : 'Instructions pour iPhone'}
              >
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Open <strong>{marketplaceDomain}</strong> in <strong>Safari</strong>
                      </>
                    ) : (
                      <>
                        Ouvrez <strong>{marketplaceDomain}</strong> dans <strong>Safari</strong>
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Tap the <strong>Share</strong> button (the square with an arrow pointing
                        up, at the bottom of the screen)
                      </>
                    ) : (
                      <>
                        Appuyez sur le bouton <strong>Partager</strong> (le carré avec la flèche
                        vers le haut, en bas de l&apos;écran)
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Scroll down and choose <strong>&quot;Add to Home Screen&quot;</strong>
                      </>
                    ) : (
                      <>
                        Faites défiler et choisissez{' '}
                        <strong>&quot;Sur l&apos;écran d&apos;accueil&quot;</strong>
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Tap <strong>&quot;Add&quot;</strong> in the top-right corner
                      </>
                    ) : (
                      <>
                        Appuyez sur <strong>&quot;Ajouter&quot;</strong> en haut à droite
                      </>
                    )}
                  </span>
                </li>
              </ol>
              <div className={css.warning} role="note">
                <span className={css.warningIcon} aria-hidden="true">
                  ⚠️
                </span>
                <span>
                  {isEnglish ? (
                    <>
                      On iPhone, this option only exists in <strong>Safari</strong>. It isn&apos;t
                      available in Chrome or Firefox.
                    </>
                  ) : (
                    <>
                      Sur iPhone, cette option n&apos;existe que dans <strong>Safari</strong>.
                      Elle n&apos;est pas disponible dans Chrome ou Firefox.
                    </>
                  )}
                </span>
              </div>
            </article>

            {/* ANDROID */}
            <article className={css.step} aria-labelledby="android-title">
              <div className={css.stepHeader}>
                <div className={css.stepNumber} aria-hidden="true">
                  2
                </div>
                <h2 className={css.stepTitle} id="android-title">
                  {isEnglish ? 'On Android (Chrome)' : 'Sur Android (Chrome)'}
                </h2>
                <span className={css.stepIcon} aria-hidden="true">
                  🤖
                </span>
              </div>
              <ol
                className={css.substeps}
                aria-label={isEnglish ? 'Instructions for Android' : 'Instructions pour Android'}
              >
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Open <strong>{marketplaceDomain}</strong> in <strong>Chrome</strong>
                      </>
                    ) : (
                      <>
                        Ouvrez <strong>{marketplaceDomain}</strong> dans <strong>Chrome</strong>
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        An <strong>&quot;Add to Home screen&quot;</strong> prompt often appears
                        automatically at the bottom of the screen
                      </>
                    ) : (
                      <>
                        Un message{' '}
                        <strong>&quot;Ajouter à l&apos;écran d&apos;accueil&quot;</strong>{' '}
                        apparaît souvent automatiquement en bas de l&apos;écran
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Otherwise, tap the <strong>⋮</strong> menu (three dots, top-right corner)
                      </>
                    ) : (
                      <>
                        Sinon, appuyez sur le menu <strong>⋮</strong> (trois points, en haut à
                        droite)
                      </>
                    )}
                  </span>
                </li>
                <li>
                  <span>
                    {isEnglish ? (
                      <>
                        Choose <strong>&quot;Install app&quot;</strong> or{' '}
                        <strong>&quot;Add to Home screen&quot;</strong>, then confirm
                      </>
                    ) : (
                      <>
                        Choisissez <strong>&quot;Installer l&apos;application&quot;</strong> ou{' '}
                        <strong>&quot;Ajouter à l&apos;écran d&apos;accueil&quot;</strong>, puis
                        confirmez
                      </>
                    )}
                  </span>
                </li>
              </ol>
              <div className={css.tip} role="note">
                <span className={css.tipIcon} aria-hidden="true">
                  💡
                </span>
                <span>
                  {isEnglish
                    ? "Once installed, always launch the app from its home screen icon — that's what gives it the full-screen, real-app feel."
                    : "Une fois installée, lancez toujours l'application à partir de son icône sur votre écran d'accueil — c'est ce qui lui donne l'apparence d'une vraie application, en plein écran."}
                </span>
              </div>
            </article>

            <div className={css.contactSection}>
              <h2>{isEnglish ? 'Need help?' : "Besoin d'aide ?"}</h2>
              <p>
                {isEnglish
                  ? "If the installation doesn't work as expected, write to us and we'll help you out."
                  : "Si l'installation ne fonctionne pas comme prévu, écrivez-nous et on vous accompagne."}
              </p>
              <a className={css.contactEmail} href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
            </div>

            <div className={css.pageFooter}>
              {brandName}
              {isEnglish ? '.ca' : '.com'} — 🌿{' '}
              {isEnglish ? 'available on iPhone and Android' : 'disponible sur iPhone et Android'}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const InstallerAppPage = compose(connect(mapStateToProps))(InstallerAppPageComponent);

export default InstallerAppPage;
